import { Injectable, OnDestroy, Optional } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { ErrorReporter } from './error-reporter';
import {
  buildTelemetryBase,
  TelemetryEvent,
  TelemetrySink,
} from './telemetry';
import { scrubValue } from './error-scrubber';

/**
 * Tenant-aware client telemetry (AC-53).
 * Reuses ErrorReporter context assembly; records Web Vitals + route timings
 * through a pluggable TelemetrySink (noop by default).
 */
@Injectable()
export class TelemetryService implements OnDestroy {
  private navStart = 0;
  private routeSub?: Subscription;
  private observers: PerformanceObserver[] = [];

  constructor(
    private errorReporter: ErrorReporter,
    private sink: TelemetrySink,
    @Optional() private router?: Router,
  ) {}

  /** Call once at app start (APP_INITIALIZER). */
  start(): void {
    this.observeWebVitals();
    this.observeRoutes();
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    for (const o of this.observers) {
      try {
        o.disconnect();
      } catch {
        // ignore
      }
    }
  }

  record(partial: Pick<TelemetryEvent, 'kind' | 'name' | 'value'> & Partial<TelemetryEvent>): void {
    const ctx = this.errorReporter.assembleContext();
    const event = scrubValue({
      ...buildTelemetryBase(ctx),
      kind: partial.kind,
      name: partial.name,
      value: partial.value,
      unit: partial.unit,
      extra: partial.extra,
    }) as TelemetryEvent;
    this.sink.record(event);
  }

  private observeRoutes(): void {
    if (!this.router) {
      return;
    }
    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationStart | NavigationEnd =>
        e instanceof NavigationStart || e instanceof NavigationEnd))
      .subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.navStart = performance.now();
          return;
        }
        if (e instanceof NavigationEnd && this.navStart > 0) {
          const ms = Math.round(performance.now() - this.navStart);
          this.navStart = 0;
          this.record({
            kind: 'route-timing',
            name: 'navigation',
            value: ms,
            unit: 'ms',
            extra: { url: scrubUrl(e.urlAfterRedirects) },
          });
        }
      });
  }

  private observeWebVitals(): void {
    if (typeof PerformanceObserver === 'undefined') {
      return;
    }
    this.observeEntry('largest-contentful-paint', 'LCP', (entries) => {
      const last = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      return last?.startTime ?? 0;
    });
    this.observeEntry('layout-shift', 'CLS', (entries) => {
      let cls = 0;
      for (const entry of entries as Array<PerformanceEntry & { value?: number; hadRecentInput?: boolean }>) {
        if (!entry.hadRecentInput) {
          cls += entry.value || 0;
        }
      }
      return cls;
    });
    this.observeEntry('first-input', 'FID', (entries) => {
      const first = entries[0] as PerformanceEntry & { processingStart?: number; startTime: number };
      if (!first?.processingStart) {
        return 0;
      }
      return first.processingStart - first.startTime;
    });
  }

  private observeEntry(
    type: string,
    name: string,
    reduce: (entries: PerformanceEntryList) => number,
  ): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const value = reduce(list.getEntries());
        if (value >= 0) {
          this.record({
            kind: 'web-vital',
            name,
            value: Math.round(value * (name === 'CLS' ? 1000 : 1)) / (name === 'CLS' ? 1000 : 1),
            unit: name === 'CLS' ? 'score' : 'ms',
          });
        }
      });
      observer.observe({ type, buffered: true } as PerformanceObserverInit);
      this.observers.push(observer);
    } catch {
      // Unsupported entry type in this browser — ignore.
    }
  }
}

/** Strip query values that may contain PII / tokens. */
export function scrubUrl(url: string): string {
  const q = url.indexOf('?');
  if (q < 0) {
    return url;
  }
  return `${url.slice(0, q)}?[REDACTED]`;
}
